import { useMemo, useState, type ReactNode } from "react";
import { useCustom } from "@refinedev/core";
import { DateField } from "@fawkes/ui";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  BedDouble,
  CalendarDays,
  Cat,
  CheckCircle2,
  Dog,
  LayoutGrid,
  Lightbulb,
  List,
  MoreHorizontal,
  PawPrint,
  Plus,
  RotateCcw,
  ShieldCheck,
  Sun,
  Users,
} from "lucide-react";

import { soortGroep, type Overzicht, type Regel } from "./velden";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

/**
 * De bezetting van het pension: voor één dag per ruimte (kennelgroep) wat er ligt, of over een periode de bezetting
 * per dag en per ruimte met de drukste dagen en wat aandacht vraagt.
 */

type Vertaal = (key: string, options?: { args?: unknown[] }) => string;

export interface Groep {
  id: number;
  naam: string | null;
  diersoortId: number | null;
  diersoort: string | null;
  accommodatie: string | null;
  kennels: number;
  capaciteit: number;
  perDag: number[];
  dagopvang: boolean;
}

interface Soort {
  diersoortId: number;
  diersoort: string | null;
  capaciteit: number;
  perDag: number[];
}

interface BezettingData {
  vestigingId: number;
  van: string;
  tot: string;
  dagen: string[];
  groepen: Groep[];
  soorten: Soort[];
  nietIngedeeldPerDag: number[];
}

type Staat = "VOL" | "BIJNA_VOL" | "RUIMTE";
type Stand = "DAG" | "PERIODE";
const MAX_DAGEN = 62;

export const procent = (bezet: number, capaciteit: number) =>
  capaciteit > 0 ? Math.round((bezet / capaciteit) * 100) : bezet > 0 ? 100 : 0;

/** De kleuren van de legenda: ruim beschikbaar, goed bezet, bijna vol, vol. */
export function tintVan(p: number): "groen" | "geel" | "oranje" | "rood" {
  if (p <= 60) return "groen";
  if (p <= 80) return "geel";
  if (p <= 95) return "oranje";
  return "rood";
}

export function staatVan(bezet: number, capaciteit: number): Staat {
  if (capaciteit > 0 ? bezet >= capaciteit : bezet > 0) return "VOL";
  if (procent(bezet, capaciteit) > 80) return "BIJNA_VOL";
  return "RUIMTE";
}

export function plusDagen(iso: string, dagen: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dagen);
  return d.toISOString().slice(0, 10);
}

function dagenTussen(van: string, tot: string): number {
  return Math.round((Date.parse(`${tot}T00:00:00Z`) - Date.parse(`${van}T00:00:00Z`)) / 86_400_000);
}

const kort = (iso: string) => `${iso.slice(8, 10)}-${iso.slice(5, 7)}`;
const lang = (iso: string) => `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}`;
function weekdag(iso: string, locale: string) {
  const w = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`)).replace(".", "");
  return w.charAt(0).toUpperCase() + w.slice(1);
}
const gemiddelde = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

function GroepIcoon({ g, size = 22 }: { g: Pick<Groep, "naam" | "dagopvang" | "diersoort">; size?: number }) {
  const naam = (g.naam ?? "").toLowerCase();
  if (g.dagopvang) return <Sun size={size} />;
  if (naam.includes("quarant") || naam.includes("isolatie")) return <ShieldCheck size={size} />;
  const soort = soortGroep(g.diersoort);
  if (soort === "HOND") return <Dog size={size} />;
  if (soort === "KAT") return <Cat size={size} />;
  return <PawPrint size={size} />;
}

function SoortIcoon({ naam, size = 24 }: { naam: string | null; size?: number }) {
  const s = soortGroep(naam);
  return s === "KAT" ? <Cat size={size} /> : s === "HOND" ? <Dog size={size} /> : <PawPrint size={size} />;
}

function Balk({ p, metProcent = true }: { p: number; metProcent?: boolean }) {
  return (
    <span className="pb-balk" data-toon={tintVan(p)}>
      <span className="pb-balk__baan">
        <span className="pb-balk__vulling" style={{ width: `${Math.min(100, p)}%` }} />
      </span>
      {metProcent && <span className="pb-balk__procent">{p}%</span>}
    </span>
  );
}

function Pil({ p }: { p: number }) {
  return (
    <span className="pb-pil" data-toon={tintVan(p)}>
      {p}%
    </span>
  );
}

// ── Het geheel: stand kiezen, gegevens ophalen, filters ───────────────────────────────────────────────

export function BezettingOnderdeel({
  t,
  locale,
  vandaag,
  onOpenPlanning,
}: {
  t: Vertaal;
  locale: string;
  vandaag: string;
  onOpenPlanning?: () => void;
}) {
  const [stand, setStand] = useState<Stand>("DAG");
  const [datum, setDatum] = useState(vandaag);
  const [van, setVan] = useState(vandaag);
  const [tot, setTot] = useState(plusDagen(vandaag, 13));
  const [diersoort, setDiersoort] = useState("");
  const [zone, setZone] = useState("");
  const [staat, setStaat] = useState<Staat | "">("");

  const begin = stand === "DAG" ? datum : van;
  const eind = stand === "DAG" ? datum : tot;
  const geldig = eind >= begin && dagenTussen(begin, eind) <= MAX_DAGEN;
  const bez = useCustom<BezettingData>({
    url: "pension/bezetting",
    method: "get",
    config: { query: { van: begin, tot: eind } },
    queryOptions: { enabled: geldig },
  });
  const ovz = useCustom<Overzicht>({
    url: "pension/overzicht",
    method: "get",
    config: { query: { datum: begin, tot: eind } },
    queryOptions: { enabled: geldig },
  });
  const seizoenen = useCustom<Array<{ id: number; naam: string | null; geldigVanaf: string; geldigTm: string; actief: boolean }>>({
    url: "pension-tarief/hoogseizoen",
    method: "get",
    queryOptions: { enabled: stand === "PERIODE" },
  });

  const data = bez.data?.data;
  const groepen = useMemo(() => (Array.isArray(data?.groepen) ? data!.groepen : []), [data]);
  const regels = useMemo(() => (Array.isArray(ovz.data?.data?.regels) ? ovz.data!.data.regels : []), [ovz.data]);
  const soortNamen = [...new Set(groepen.filter((g) => !g.dagopvang).map((g) => g.diersoort).filter((x): x is string => !!x))];
  const gefilterd = groepen
    .filter((g) => !diersoort || g.diersoort === diersoort)
    .filter((g) => !zone || String(g.id) === zone);
  const filtersAan = !!(diersoort || zone || staat);
  const wis = () => {
    setDiersoort("");
    setZone("");
    setStaat("");
  };

  // Vakantieperiode: het eerstvolgende hoogseizoen dat nog niet voorbij is.
  const vakantie = (Array.isArray(seizoenen.data?.data) ? seizoenen.data!.data : [])
    .filter((s) => s.actief && s.geldigTm >= vandaag)
    .sort((a, b) => a.geldigVanaf.localeCompare(b.geldigVanaf))[0];
  const periodeDagen = dagenTussen(van, tot) + 1;
  const kiesPreset = (n: number) => setTot(plusDagen(van, n - 1));

  return (
    <section className="pb" aria-label={t("pensionOverzichten.onderdeel.BEZETTING")}>
      <header className="pb-kop">
        <span className="po-kop__icoon" aria-hidden="true">
          <BarChart3 size={28} />
        </span>
        <div className="po-kop__tekst">
          <h1>{t("pensionOverzichten.kop.BEZETTING")}</h1>
          <p>{t(stand === "DAG" ? "pensionOverzichten.bez.ondertitel" : "pensionOverzichten.bez.ondertitel.periode")}</p>
        </div>
        <span className="po-segment" role="group" aria-label={t("pensionOverzichten.bez.stand")}>
          {(["DAG", "PERIODE"] as Stand[]).map((s) => (
            <button key={s} type="button" aria-pressed={stand === s} onClick={() => setStand(s)}>
              {t(`pensionOverzichten.bez.stand.${s}`)}
            </button>
          ))}
        </span>
        {onOpenPlanning && (
          <button type="button" className="po-knop" onClick={onOpenPlanning}>
            <CalendarDays size={16} aria-hidden="true" />
            {t("pensionOverzichten.bezetting.planning")}
          </button>
        )}
      </header>

      <section className="po-filters" aria-label={t("pensionOverzichten.filters")}>
        <div className="po-filters__rij">
          {stand === "DAG" ? (
            <div className="po-veld pb-datum">
              <span>{t("pensionOverzichten.datum")}</span>
              <span className="pb-datum__rij">
                <button type="button" className="po-knop" aria-pressed={datum === vandaag} onClick={() => setDatum(vandaag)}>
                  <CalendarDays size={15} aria-hidden="true" />
                  {t("pensionOverzichten.bez.vandaag")}
                </button>
                <DateField value={datum} aria-label={t("pensionOverzichten.datum")} onChange={(e) => e.target.value && setDatum(e.target.value)} />
              </span>
            </div>
          ) : (
            <>
              <label className="po-veld pb-datumveld">
                <span>{t("pensionOverzichten.bez.van")}</span>
                <DateField
                  value={van}
                  aria-label={t("pensionOverzichten.bez.van")}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    // De lengte van de periode blijft staan als je het begin verschuift.
                    setTot(plusDagen(v, Math.max(0, periodeDagen - 1)));
                    setVan(v);
                  }}
                />
              </label>
              <label className="po-veld pb-datumveld">
                <span>{t("pensionOverzichten.bez.tot")}</span>
                <DateField value={tot} aria-label={t("pensionOverzichten.bez.tot")} onChange={(e) => e.target.value && setTot(e.target.value)} />
              </label>
              <span className="po-segment pb-presets" role="group" aria-label={t("pensionOverzichten.bez.presets")}>
                {[7, 14, 30].map((n) => (
                  <button key={n} type="button" aria-pressed={periodeDagen === n} onClick={() => kiesPreset(n)}>
                    {t("pensionOverzichten.bez.nDagen", { args: [n] })}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={!vakantie}
                  title={vakantie ? `${vakantie.naam ?? ""} ${lang(vakantie.geldigVanaf)} – ${lang(vakantie.geldigTm)}` : t("pensionOverzichten.bez.geenVakantie")}
                  aria-pressed={!!vakantie && van === vakantie.geldigVanaf}
                  onClick={() => {
                    if (!vakantie) return;
                    const begin = vakantie.geldigVanaf < vandaag ? vandaag : vakantie.geldigVanaf;
                    const eind = vakantie.geldigTm > plusDagen(begin, MAX_DAGEN) ? plusDagen(begin, MAX_DAGEN) : vakantie.geldigTm;
                    setVan(begin);
                    setTot(eind);
                  }}
                >
                  {t("pensionOverzichten.bez.vakantie")}
                </button>
              </span>
            </>
          )}
          <Keuze label={t("pensionOverzichten.soort")} waarde={diersoort} onChange={setDiersoort} alle={t("pensionOverzichten.alle")} opties={soortNamen.map((s) => [s, s])} />
          <Keuze label={t("pensionOverzichten.bez.zone")} waarde={zone} onChange={setZone} alle={t("pensionOverzichten.alle")} opties={groepen.map((g) => [String(g.id), g.naam ?? "—"])} />
          <Keuze
            label={t("pensionOverzichten.veld.status")}
            waarde={staat}
            onChange={(v) => setStaat(v as Staat | "")}
            alle={t("pensionOverzichten.alle")}
            opties={(["VOL", "BIJNA_VOL", "RUIMTE"] as Staat[]).map((s) => [s, t(`pensionOverzichten.bez.staat.${s}`)])}
          />
          <span className="po-vul" />
          <button type="button" className="po-knop" disabled={!filtersAan} onClick={wis}>
            <RotateCcw size={15} aria-hidden="true" />
            {t("pensionOverzichten.wis")}
          </button>
        </div>
        {!geldig && <p className="pb-fout">{t("pensionOverzichten.bez.ongeldig", { args: [MAX_DAGEN] })}</p>}
      </section>

      {bez.isLoading || ovz.isLoading || !data ? (
        <p className="po-leeg">{bez.isError ? t("pensionOverzichten.loadFailed") : t("common.loading")}</p>
      ) : groepen.length === 0 ? (
        <p className="po-leeg">{t("pensionOverzichten.bez.geenRuimtes")}</p>
      ) : stand === "DAG" ? (
        <Dag
          t={t}
          data={data}
          groepen={gefilterd}
          staat={staat}
          filtersAan={filtersAan}
          regels={regels}
          opVandaag={datum === vandaag}
          onOpenPlanning={onOpenPlanning}
        />
      ) : (
        <Periode t={t} locale={locale} data={data} groepen={gefilterd} staat={staat} filtersAan={filtersAan} regels={regels} onOpenPlanning={onOpenPlanning} />
      )}
    </section>
  );
}

/** De pensiongroepen (zonder dagopvang) per dag bij elkaar, met de dieren zonder hok erbij als er niet gefilterd is. */
function totaalPerDag(data: BezettingData, groepen: Groep[], filtersAan: boolean) {
  const pension = groepen.filter((g) => !g.dagopvang);
  const capaciteit = pension.reduce((s, g) => s + g.capaciteit, 0);
  const bezet = data.dagen.map(
    (_, i) => pension.reduce((s, g) => s + (g.perDag[i] ?? 0), 0) + (filtersAan ? 0 : (data.nietIngedeeldPerDag[i] ?? 0)),
  );
  return { capaciteit, bezet };
}

function perSoortTekst(lijst: Regel[], t: Vertaal) {
  const tel = new Map<string, number>();
  lijst.forEach((r) => {
    const s = soortGroep(r.diersoort);
    tel.set(s, (tel.get(s) ?? 0) + 1);
  });
  return (["HOND", "KAT", "OVERIG"] as const)
    .filter((s) => tel.get(s))
    .map((s) => t(`pensionOverzichten.aantal.${s}.${tel.get(s) === 1 ? "een" : "meer"}`, { args: [tel.get(s)] }))
    .join(" • ");
}

// ── Eén dag ───────────────────────────────────────────────────────────────────────────────────────────

function Dag({
  t,
  data,
  groepen,
  staat,
  filtersAan,
  regels,
  opVandaag,
  onOpenPlanning,
}: {
  t: Vertaal;
  data: BezettingData;
  groepen: Groep[];
  staat: Staat | "";
  filtersAan: boolean;
  regels: Regel[];
  opVandaag: boolean;
  onOpenPlanning?: () => void;
}) {
  const [weergave, setWeergave] = useState<"KAARTEN" | "LIJST">("KAARTEN");
  const [sortering, setSortering] = useState<"HOOG" | "LAAG" | "VRIJ" | "NAAM">("HOOG");
  const bezet0 = (g: Groep) => g.perDag[0] ?? 0;
  const { capaciteit, bezet } = totaalPerDag(data, groepen, filtersAan);
  const totaal = bezet[0] ?? 0;
  const nietIngedeeld = data.nietIngedeeldPerDag[0] ?? 0;
  const pension = regels.filter((r) => r.soort === "PENSION" && r.status !== "GEPLAND");
  const aankomsten = pension.filter((r) => r.aankomst);
  const vertrekken = pension.filter((r) => r.vertrek);

  const zichtbaar = groepen
    .filter((g) => !staat || staatVan(bezet0(g), g.capaciteit) === staat)
    .sort((a, b) => {
      const pa = procent(bezet0(a), a.capaciteit);
      const pb = procent(bezet0(b), b.capaciteit);
      if (sortering === "LAAG") return pa - pb;
      if (sortering === "VRIJ") return b.capaciteit - bezet0(b) - (a.capaciteit - bezet0(a));
      if (sortering === "NAAM") return (a.naam ?? "").localeCompare(b.naam ?? "", "nl", { numeric: true });
      return pb - pa;
    });
  const bijnaVol = groepen.filter((g) => staatVan(bezet0(g), g.capaciteit) !== "RUIMTE");
  const meestBezet = [...groepen].sort((a, b) => procent(bezet0(b), b.capaciteit) - procent(bezet0(a), a.capaciteit)).slice(0, 3);
  const dagopvang = groepen.find((g) => g.dagopvang);

  return (
    <>
      <div className="pb-kpis">
        <Kpi icoon={<PawPrint size={24} />} label={t("pensionOverzichten.bez.totaal")} waarde={`${totaal} / ${capaciteit}`}>
          <Balk p={procent(totaal, capaciteit)} />
        </Kpi>
        <Kpi icoon={<BedDouble size={24} />} label={t("pensionOverzichten.bez.vrij")} waarde={Math.max(0, capaciteit - totaal)}>
          <span className="pb-kpi__onder">{t("pensionOverzichten.bez.vanTotaal", { args: [capaciteit] })}</span>
        </Kpi>
        <Kpi icoon={<ArrowRight size={24} />} toon="groen" label={t(opVandaag ? "pensionOverzichten.bez.aankomstenVandaag" : "pensionOverzichten.bez.aankomsten")} waarde={aankomsten.length}>
          <span className="pb-kpi__onder">{t("pensionOverzichten.bez.dieren")}</span>
        </Kpi>
        <Kpi icoon={<ArrowRight size={24} />} toon="rood" label={t(opVandaag ? "pensionOverzichten.bez.vertrekkenVandaag" : "pensionOverzichten.bez.vertrekken")} waarde={vertrekken.length}>
          <span className="pb-kpi__onder">{t("pensionOverzichten.bez.dieren")}</span>
        </Kpi>
        {data.soorten
          .filter((s) => s.capaciteit > 0 || (s.perDag[0] ?? 0) > 0)
          .map((s) => (
            <Kpi key={s.diersoortId} icoon={<SoortIcoon naam={s.diersoort} />} label={meervoudSoort(s.diersoort, t)} waarde={s.capaciteit > 0 ? `${s.perDag[0] ?? 0} / ${s.capaciteit}` : (s.perDag[0] ?? 0)}>
              {s.capaciteit > 0 ? <Balk p={procent(s.perDag[0] ?? 0, s.capaciteit)} /> : <span className="pb-kpi__onder">{t("pensionOverzichten.bez.geenRuimte")}</span>}
            </Kpi>
          ))}
      </div>

      <div className="pb-sectiekop">
        <h2>{t("pensionOverzichten.bez.perRuimte")}</h2>
        <span className="po-vul" />
        <span className="po-segment" role="group" aria-label={t("pensionOverzichten.weergave")}>
          <button type="button" aria-pressed={weergave === "KAARTEN"} onClick={() => setWeergave("KAARTEN")}>
            <LayoutGrid size={15} aria-hidden="true" /> {t("pensionOverzichten.bez.kaarten")}
          </button>
          <button type="button" aria-pressed={weergave === "LIJST"} onClick={() => setWeergave("LIJST")}>
            <List size={15} aria-hidden="true" /> {t("pensionOverzichten.weergave.LIJST")}
          </button>
        </span>
        <label className="po-sorteer">
          <span className="po-zacht">{t("pensionOverzichten.sorteren")}</span>
          <select value={sortering} onChange={(e) => setSortering(e.target.value as typeof sortering)}>
            {(["HOOG", "LAAG", "VRIJ", "NAAM"] as const).map((s) => (
              <option key={s} value={s}>
                {t(`pensionOverzichten.bez.sortering.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {weergave === "KAARTEN" ? (
        <div className="pb-kaarten">
          {zichtbaar.map((g) => (
            <RuimteKaart key={g.id} g={g} bezet={bezet0(g)} t={t} />
          ))}
          {nietIngedeeld > 0 && !filtersAan && (
            <article className="pb-kaart pb-kaart--let" aria-label={t("pensionOverzichten.bez.nietIngedeeld")}>
              <div className="pb-kaart__kop">
                <span className="pb-kaart__icoon" aria-hidden="true">
                  <AlertCircle size={22} />
                </span>
                <div>
                  <h3>{t("pensionOverzichten.bez.nietIngedeeld")}</h3>
                  <p>{t("pensionOverzichten.bez.nietIngedeeld.uitleg")}</p>
                </div>
              </div>
              <strong className="pb-kaart__getal">{t(nietIngedeeld === 1 ? "pensionOverzichten.aantalDieren.een" : "pensionOverzichten.aantalDieren", { args: [nietIngedeeld] })}</strong>
              {onOpenPlanning && (
                <button type="button" className="po-knop" onClick={onOpenPlanning}>
                  {t("pensionOverzichten.bezetting.planning")}
                </button>
              )}
            </article>
          )}
          {!filtersAan && (
            <article className="pb-kaart pb-kaart--nieuw">
              <span className="pb-kaart__plus" aria-hidden="true">
                <Plus size={18} />
              </span>
              <strong>{t("pensionOverzichten.bez.nieuw")}</strong>
              <p>{t("pensionOverzichten.bez.nieuw.uitleg")}</p>
              {onOpenPlanning && (
                <button type="button" className="po-knop" onClick={onOpenPlanning}>
                  {t("pensionOverzichten.bezetting.planning")}
                </button>
              )}
            </article>
          )}
        </div>
      ) : (
        <div className="po-lijstwrap pb-lijstwrap">
          <IndeelbareTabel
            tabelKey="pension-bezetting-per-dag-ruimtes"
            columns={[
              {
                key: "ruimte", header: t("pensionOverzichten.bez.ruimte"),
                cell: (g) => (
                  <span className="pb-lijst__naam">
                    <span className="pb-kaart__icoon pb-kaart__icoon--klein" aria-hidden="true">
                      <GroepIcoon g={g} size={16} />
                    </span>
                    <strong>{g.naam}</strong>
                  </span>
                ),
              },
              {
                key: "soort", header: t("pensionOverzichten.soort"),
                cell: (g) => (g.dagopvang ? t("pensionOverzichten.chip.dagopvang") : (g.diersoort ?? "—")),
              },
              {
                key: "kennels", header: t("pensionOverzichten.bez.kennels"), align: "right",
                cell: (g) => <span className="po-getal">{g.kennels}</span>,
              },
              {
                key: "bezet", header: t("pensionOverzichten.bez.bezet"), align: "right",
                cell: (g) => <span className="po-getal">{bezet0(g)} / {g.capaciteit}</span>,
              },
              {
                key: "vrij", header: t("pensionOverzichten.bez.vrijKort"), align: "right",
                cell: (g) => <span className="po-getal">{Math.max(0, g.capaciteit - bezet0(g))}</span>,
              },
              {
                key: "graad", header: t("pensionOverzichten.bez.graad"),
                cell: (g) => (
                  <span className="pb-lijst__balk">
                    <Balk p={procent(bezet0(g), g.capaciteit)} />
                  </span>
                ),
              },
              {
                key: "status", header: t("pensionOverzichten.veld.status"),
                cell: (g) => <StaatLabel bezet={bezet0(g)} capaciteit={g.capaciteit} t={t} />,
              },
            ]}
            rows={zichtbaar}
            getRowKey={(g) => String(g.id)}
            empty=""
          />
        </div>
      )}

      <div className="pb-onder">
        <section className="pb-paneel" aria-label={t(opVandaag ? "pensionOverzichten.bez.vandaag" : "pensionOverzichten.bez.dezeDag")}>
          <PaneelKop icoon={<CalendarDays size={20} />} titel={t(opVandaag ? "pensionOverzichten.bez.vandaag" : "pensionOverzichten.bez.dezeDag")} uitleg={t("pensionOverzichten.bez.vandaag.uitleg")} />
          <div className="pb-mini">
            <Mini icoon={<ArrowRight size={22} />} toon="groen" waarde={aankomsten.length} label={t("pensionOverzichten.bez.mini.aankomsten")} onder={perSoortTekst(aankomsten, t)} />
            <Mini icoon={<ArrowLeft size={22} />} toon="rood" waarde={vertrekken.length} label={t("pensionOverzichten.bez.mini.vertrekken")} onder={perSoortTekst(vertrekken, t)} />
            <Mini
              icoon={<AlertCircle size={22} />}
              toon="oranje"
              waarde={bijnaVol.length}
              label={t("pensionOverzichten.bez.mini.bijnaVol")}
              onder={bijnaVol.map((g) => g.naam).join(", ")}
              let={bijnaVol.length > 0}
            />
            {dagopvang && (
              <Mini
                icoon={<Sun size={22} />}
                waarde={bezet0(dagopvang)}
                label={t("pensionOverzichten.bez.mini.dagopvang")}
                onder={t("pensionOverzichten.bez.vanPlekken", { args: [dagopvang.capaciteit] })}
              />
            )}
          </div>
        </section>
        <section className="pb-paneel pb-paneel--top" aria-label={t("pensionOverzichten.bez.meestBezet")}>
          <PaneelKop icoon={<BarChart3 size={20} />} titel={t("pensionOverzichten.bez.meestBezet")} uitleg={t("pensionOverzichten.bez.meestBezet.uitleg")} />
          <ol className="pb-top">
            {meestBezet.map((g, i) => (
              <li key={g.id}>
                <span className="pb-top__plek">{i + 1}</span>
                <span className="pb-top__naam">{g.naam}</span>
                <span className="pb-top__getal">
                  {bezet0(g)} / {g.capaciteit}
                </span>
                <Balk p={procent(bezet0(g), g.capaciteit)} />
              </li>
            ))}
          </ol>
        </section>
      </div>
    </>
  );
}

// ── Een periode ───────────────────────────────────────────────────────────────────────────────────────

function Periode({
  t,
  locale,
  data,
  groepen,
  staat,
  filtersAan,
  regels,
  onOpenPlanning,
}: {
  t: Vertaal;
  locale: string;
  data: BezettingData;
  groepen: Groep[];
  staat: Staat | "";
  filtersAan: boolean;
  regels: Regel[];
  onOpenPlanning?: () => void;
}) {
  const dagen = data.dagen;
  const { capaciteit, bezet } = totaalPerDag(data, groepen, filtersAan);
  const pct = bezet.map((b) => procent(b, capaciteit));
  const hoogste = Math.max(0, ...pct);
  const drukste = pct.indexOf(hoogste);
  const pension = regels.filter((r) => r.soort === "PENSION");
  const aankomsten = pension.filter((r) => r.van >= data.van && r.van <= data.tot);
  const vertrekken = pension.filter((r) => r.tot >= data.van && r.tot <= data.tot);

  const ruimtes = groepen.map((g) => {
    const p = g.perDag.map((b) => procent(b, g.capaciteit));
    const volOp = g.perDag.findIndex((b) => staatVan(b, g.capaciteit) === "VOL");
    const bijna = g.perDag.findIndex((b) => staatVan(b, g.capaciteit) !== "RUIMTE");
    const piek = Math.max(0, ...p);
    const s: Staat = volOp >= 0 ? "VOL" : bijna >= 0 ? "BIJNA_VOL" : "RUIMTE";
    return { g, p, gem: Math.round(gemiddelde(p)), piek, volOp, bijna, drukDagen: p.filter((x) => x > 80).length, staat: s };
  });
  const zichtbaar = ruimtes.filter((r) => !staat || r.staat === staat);

  const druksteDagen = dagen
    .map((d, i) => ({ d, p: pct[i], b: bezet[i] }))
    .sort((a, b) => b.p - a.p || a.d.localeCompare(b.d))
    .slice(0, 5);

  // Wat de balie moet weten: wanneer een ruimte volloopt, en hoeveel ruimte er op de drukste dag nog is.
  const punten: Array<{ toon: "rood" | "oranje" | "accent"; tekst: string }> = [];
  for (const r of ruimtes) {
    if (r.volOp >= 0) {
      punten.push({ toon: "rood", tekst: t("pensionOverzichten.bez.punt.vol", { args: [kort(dagen[r.volOp]), r.g.naam ?? "", r.g.perDag[r.volOp], r.g.capaciteit] }) });
    } else if (r.bijna >= 0) {
      punten.push({ toon: "oranje", tekst: t("pensionOverzichten.bez.punt.bijnaVol", { args: [kort(dagen[r.bijna]), r.g.naam ?? "", r.g.perDag[r.bijna], r.g.capaciteit] }) });
    }
  }
  if (drukste >= 0 && dagen.length > 1) {
    punten.push({ toon: "accent", tekst: t("pensionOverzichten.bez.punt.drukste", { args: [Math.max(0, capaciteit - bezet[drukste]), kort(dagen[drukste])] }) });
  }
  if (punten.length === 0) punten.push({ toon: "accent", tekst: t("pensionOverzichten.bez.punt.rustig") });

  return (
    <>
      <div className="pb-kpis">
        <Kpi label={t("pensionOverzichten.bez.gem")} waarde={`${Math.round(gemiddelde(pct))}%`}>
          <Balk p={Math.round(gemiddelde(pct))} metProcent={false} />
        </Kpi>
        <Kpi label={t("pensionOverzichten.bez.hoogste")} waarde={`${hoogste}%`}>
          <Balk p={hoogste} metProcent={false} />
        </Kpi>
        <Kpi label={t("pensionOverzichten.bez.vrijDrukste")} waarde={Math.max(0, capaciteit - (bezet[drukste] ?? 0))} rechts={<Users size={26} />}>
          <span className="pb-kpi__onder">{t("pensionOverzichten.bez.vanTotaal", { args: [capaciteit] })}</span>
        </Kpi>
        <Kpi icoon={<ArrowLeft size={22} />} toon="groen" label={t("pensionOverzichten.bez.aankomsten")} waarde={aankomsten.length}>
          <span className="pb-kpi__onder">{t("pensionOverzichten.bez.inPeriode")}</span>
        </Kpi>
        <Kpi icoon={<ArrowRight size={22} />} toon="rood" label={t("pensionOverzichten.bez.vertrekken")} waarde={vertrekken.length}>
          <span className="pb-kpi__onder">{t("pensionOverzichten.bez.inPeriode")}</span>
        </Kpi>
        {data.soorten
          .filter((s) => s.capaciteit > 0 || s.perDag.some((x) => x > 0))
          .map((s) => {
            const gem = Math.round(gemiddelde(s.perDag));
            return (
              <Kpi key={s.diersoortId} icoon={<SoortIcoon naam={s.diersoort} />} label={meervoudSoort(s.diersoort, t)} waarde={s.capaciteit > 0 ? `${gem} / ${s.capaciteit}` : gem}>
                {s.capaciteit > 0 ? <Balk p={procent(gem, s.capaciteit)} /> : <span className="pb-kpi__onder">{t("pensionOverzichten.bez.geenRuimte")}</span>}
              </Kpi>
            );
          })}
      </div>

      <section className="pb-paneel pb-perdag" aria-label={t("pensionOverzichten.bez.perDag")}>
        <div className="pb-perdag__kop">
          <div>
            <h2>{t("pensionOverzichten.bez.perDag")}</h2>
            <p>{t("pensionOverzichten.bez.perDag.uitleg")}</p>
          </div>
          <ul className="pb-legenda">
            {(["groen", "geel", "oranje", "rood"] as const).map((k) => (
              <li key={k} data-toon={k}>
                <span aria-hidden="true" />
                <strong>{t(`pensionOverzichten.bez.legenda.${k}`)}</strong>
                <small>{t(`pensionOverzichten.bez.legenda.${k}.uitleg`)}</small>
              </li>
            ))}
          </ul>
        </div>
        <div className="pb-perdag__wrap">
          <table className="pb-perdag__tabel">
            <thead>
              <tr>
                <th scope="col">{t("pensionOverzichten.datum")}</th>
                {dagen.map((d) => (
                  <th key={d} scope="col">
                    <span>{weekdag(d, locale)}</span>
                    <span>{kort(d)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{t("pensionOverzichten.bez.rij.bezetting")}</th>
                {pct.map((p, i) => (
                  <td key={dagen[i]}>
                    <Pil p={p} />
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">{t("pensionOverzichten.bez.rij.bezet")}</th>
                {bezet.map((b, i) => (
                  <td key={dagen[i]}>
                    {b} / {capaciteit}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">{t("pensionOverzichten.bez.rij.vrij")}</th>
                {bezet.map((b, i) => (
                  <td key={dagen[i]}>
                    <strong>{Math.max(0, capaciteit - b)}</strong>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div className="pb-periode">
        <section className="pb-paneel" aria-label={t("pensionOverzichten.bez.perRuimte.periode")}>
          <div className="pb-perdag__kop">
            <div>
              <h2>{t("pensionOverzichten.bez.perRuimte.periode")}</h2>
              <p>{t("pensionOverzichten.bez.perRuimte.periode.uitleg")}</p>
            </div>
          </div>
          <div className="po-lijstwrap">
            <IndeelbareTabel
              tabelKey="pension-bezetting-per-ruimte"
              columns={[
                {
                  key: "ruimte", header: t("pensionOverzichten.bez.ruimte"),
                  cell: (r) => (
                    <span className="pb-lijst__naam">
                      <span className="pb-kaart__icoon pb-kaart__icoon--klein" aria-hidden="true">
                        <GroepIcoon g={r.g} size={16} />
                      </span>
                      <span>
                        <strong>{r.g.naam}</strong>
                        <small>{r.g.accommodatie ?? (r.g.dagopvang ? t("pensionOverzichten.chip.dagopvang") : r.g.diersoort)}</small>
                      </span>
                    </span>
                  ),
                },
                {
                  key: "capaciteit", header: t("pensionOverzichten.bez.capaciteit"), align: "right",
                  cell: (r) => <span className="po-getal">{r.g.capaciteit}</span>,
                },
                {
                  key: "gem", header: t("pensionOverzichten.bez.gem"),
                  cell: (r) => (
                    <span className="pb-ruimtes__balk">
                      <strong>{r.gem}%</strong>
                      <Balk p={r.gem} metProcent={false} />
                    </span>
                  ),
                },
                {
                  key: "hoogste", header: t("pensionOverzichten.bez.hoogste"),
                  cell: (r) => (
                    <span className="pb-ruimtes__balk">
                      <strong>{r.piek}%</strong>
                      <Balk p={r.piek} metProcent={false} />
                    </span>
                  ),
                },
                {
                  key: "dagenVol", header: t("pensionOverzichten.bez.dagenVol"), align: "right",
                  cell: (r) => <span className="po-getal">{r.drukDagen} / {dagen.length}</span>,
                },
                {
                  key: "perDag", header: t("pensionOverzichten.bez.perDag.kort"),
                  cell: (r) => (
                    <span className="pb-spark" role="img" aria-label={t("pensionOverzichten.bez.spark", { args: [r.g.naam ?? ""] })}>
                      {r.p.map((p, i) => (
                        <span key={dagen[i]} data-toon={tintVan(p)} style={{ height: `${Math.max(12, Math.min(100, p))}%` }} title={`${kort(dagen[i])}: ${p}%`} />
                      ))}
                    </span>
                  ),
                },
                {
                  key: "status", header: t("pensionOverzichten.veld.status"),
                  cell: (r) => (
                    <span className="pb-staat" data-staat={r.staat}>
                      {r.staat === "RUIMTE" ? <CheckCircle2 size={14} aria-hidden="true" /> : <AlertCircle size={14} aria-hidden="true" />}
                      {r.staat === "VOL"
                        ? t("pensionOverzichten.bez.volOp", { args: [kort(dagen[r.volOp])] })
                        : t(r.staat === "BIJNA_VOL" ? "pensionOverzichten.bez.staat.BIJNA_VOL" : "pensionOverzichten.bez.plek")}
                    </span>
                  ),
                },
                {
                  key: "acties", header: "", vast: true,
                  cell: () => (onOpenPlanning ? (
                    <button type="button" className="po-icoonknop" aria-label={t("pensionOverzichten.bezetting.planning")} title={t("pensionOverzichten.bezetting.planning")} onClick={onOpenPlanning}>
                      <MoreHorizontal size={16} aria-hidden="true" />
                    </button>
                  ) : null),
                },
              ]}
              rows={zichtbaar}
              getRowKey={(r) => String(r.g.id)}
              empty=""
            />
          </div>
        </section>

        <div className="pb-zijkant">
          <section className="pb-paneel" aria-label={t("pensionOverzichten.bez.drukste")}>
            <PaneelKop icoon={<CalendarDays size={20} />} titel={t("pensionOverzichten.bez.drukste")} uitleg={t("pensionOverzichten.bez.drukste.uitleg")} />
            <ol className="pb-drukste">
              {druksteDagen.map((x, i) => (
                <li key={x.d}>
                  <span className="pb-drukste__plek">{i + 1}</span>
                  <span className="pb-drukste__dag">
                    {weekdag(x.d, locale)} {kort(x.d)}
                  </span>
                  <Pil p={x.p} />
                  <span className="pb-drukste__getal">
                    {x.b} / {capaciteit}
                  </span>
                </li>
              ))}
            </ol>
          </section>
          <section className="pb-paneel" aria-label={t("pensionOverzichten.bez.punten")}>
            <PaneelKop icoon={<Lightbulb size={20} />} titel={t("pensionOverzichten.bez.punten")} uitleg={t("pensionOverzichten.bez.punten.uitleg")} />
            <ul className="pb-punten">
              {punten.map((p, i) => (
                <li key={i} data-toon={p.toon}>
                  {p.tekst}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </>
  );
}

// ── Bouwstenen ────────────────────────────────────────────────────────────────────────────────────────

function meervoudSoort(naam: string | null, t: Vertaal) {
  return t(`pensionOverzichten.bez.soort.${soortGroep(naam)}`, { args: [naam ?? ""] });
}

function StaatLabel({ bezet, capaciteit, t }: { bezet: number; capaciteit: number; t: Vertaal }) {
  const s = staatVan(bezet, capaciteit);
  return (
    <span className="pb-staat" data-staat={s}>
      {s === "RUIMTE" ? <CheckCircle2 size={14} aria-hidden="true" /> : <AlertCircle size={14} aria-hidden="true" />}
      {t(`pensionOverzichten.bez.staat.${s}`)}
    </span>
  );
}

function RuimteKaart({ g, bezet, t }: { g: Groep; bezet: number; t: Vertaal }) {
  const vrij = Math.max(0, g.capaciteit - bezet);
  const p = procent(bezet, g.capaciteit);
  const onder = [
    g.accommodatie ?? (g.dagopvang ? t("pensionOverzichten.chip.dagopvang") : g.diersoort),
    t(`pensionOverzichten.bez.aantalKennels.${g.kennels === 1 ? "een" : "meer"}`, { args: [g.kennels] }),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <article className="pb-kaart" data-toon={tintVan(p)} aria-label={g.naam ?? undefined}>
      <div className="pb-kaart__kop">
        <span className="pb-kaart__icoon" aria-hidden="true">
          <GroepIcoon g={g} />
        </span>
        <div>
          <h3>{g.naam}</h3>
          <p>{onder}</p>
        </div>
        <StaatLabel bezet={bezet} capaciteit={g.capaciteit} t={t} />
      </div>
      <strong className="pb-kaart__getal">
        {bezet} / {g.capaciteit}
      </strong>
      <Balk p={p} />
      <span className="pb-kaart__vrij">
        <BedDouble size={16} aria-hidden="true" />
        {t("pensionOverzichten.bez.nVrij", { args: [vrij] })}
      </span>
    </article>
  );
}

function PaneelKop({ icoon, titel, uitleg }: { icoon: ReactNode; titel: string; uitleg: string }) {
  return (
    <div className="pb-paneel__kop">
      <span className="pb-kaart__icoon" aria-hidden="true">
        {icoon}
      </span>
      <div>
        <h3>{titel}</h3>
        <p>{uitleg}</p>
      </div>
    </div>
  );
}

function Kpi({
  icoon,
  rechts,
  label,
  waarde,
  toon,
  children,
}: {
  icoon?: ReactNode;
  rechts?: ReactNode;
  label: string;
  waarde: ReactNode;
  toon?: "groen" | "rood";
  children?: ReactNode;
}) {
  return (
    <div className="pb-kpi" data-toon={toon}>
      {icoon && (
        <span className="pb-kpi__icoon" aria-hidden="true">
          {icoon}
        </span>
      )}
      <div className="pb-kpi__tekst">
        <span className="pb-kpi__label">{label}</span>
        <strong className="pb-kpi__waarde">{waarde}</strong>
        {children}
      </div>
      {rechts && (
        <span className="pb-kpi__rechts" aria-hidden="true">
          {rechts}
        </span>
      )}
    </div>
  );
}

function Mini({ icoon, waarde, label, onder, toon, let: aandacht }: { icoon: ReactNode; waarde: number; label: string; onder?: string; toon?: "groen" | "rood" | "oranje"; let?: boolean }) {
  return (
    <div className="pb-mini__vak" data-toon={toon} data-let={aandacht || undefined}>
      <span className="pb-mini__icoon" aria-hidden="true">
        {icoon}
      </span>
      <div>
        <strong>{waarde}</strong>
        <span>{label}</span>
        {onder && <small>{onder}</small>}
      </div>
    </div>
  );
}

function Keuze({ label, waarde, onChange, alle, opties }: { label: string; waarde: string; onChange: (v: string) => void; alle: string; opties: Array<[string, string]> }) {
  return (
    <label className="po-veld">
      <span>{label}</span>
      <select value={waarde} onChange={(e) => onChange(e.target.value)}>
        <option value="">{alle}</option>
        {opties.map(([v, tekst]) => (
          <option key={v} value={v}>
            {tekst}
          </option>
        ))}
      </select>
    </label>
  );
}
